/**
 * PHA-1119: who gets a "Regenerate Care Plan" button, and does approving that regeneration
 * enroll the patient or update an existing enrollment?
 *
 * These two questions live in three files that have to agree — PatientDetail.tsx renders the
 * chart CTAs, OnboardingWorkflow.tsx decides what the wizard resumes as, and
 * CarePlanApproval.tsx decides between first-time consent and the PHA-726/897 acknowledgment.
 * PHA-1073 was exactly the failure of two of them to agree, so the shared predicates live
 * here, out of the component tree, where they can be read and tested on their own.
 */

/** What the chart header offers a patient, given their care-plan state. */
export interface OnboardingCarePlanCta {
  /** "Continue Onboarding" — resume the wizard wherever the clinician left off. */
  showContinueOnboarding: boolean;
  /** "Regenerate Care Plan" / "Complete Care Plan Regeneration". */
  showRegeneration: boolean;
  /**
   * Does the regeneration button re-enter the saved draft (PHA-1050) instead of opening the
   * source picker? True only for an enrolled patient with a regeneration already in flight,
   * where opening the picker would generate a second plan on top of the one being edited.
   */
  regenerationResumesDraft: boolean;
}

/**
 * PHA-1119, as specified on the ticket:
 *
 *   1. Mid-onboarding AND a care plan already exists (as a draft) → BOTH buttons. Continue
 *      Onboarding resumes; Regenerate Care Plan starts the same process any patient with a
 *      care plan gets — source picker, Upload / Review, condition selection.
 *   2. Mid-onboarding with NO care plan yet → Continue Onboarding ONLY. Nothing to
 *      regenerate, and the first-time wizard already walks the whole sequence.
 *
 * The bug: on Wallabout ODA patient 116325, whose plan was left as a draft in July, Continue
 * Onboarding resumed straight onto the Care Plan step. That skipped Upload and the
 * condition-selection step, so a new plan was generated across every problem on file with no
 * way to narrow it and no way to re-upload a current problem/medication list.
 *
 * `hasApprovedCarePlan` is `!needsContinueOnboarding(patient)` — see PatientDetail.tsx.
 * `hasManualDraftCarePlan` is null until the probe returns (and stays null if it fails), which
 * reads as "no draft" so the second button never flickers in and out.
 */
export function getOnboardingCarePlanCta({
  hasApprovedCarePlan,
  hasManualDraftCarePlan,
}: {
  hasApprovedCarePlan: boolean;
  hasManualDraftCarePlan: boolean | null;
}): OnboardingCarePlanCta {
  const hasDraft = hasManualDraftCarePlan === true;
  return {
    showContinueOnboarding: !hasApprovedCarePlan,
    showRegeneration: hasApprovedCarePlan || hasDraft,
    regenerationResumesDraft: hasApprovedCarePlan && hasDraft,
  };
}

/**
 * PHA-1119: is approving this care plan the patient's ENROLLMENT, or an update to a plan they
 * are already enrolled under?
 *
 * `isCarePlanRefresh` used to answer two questions at once. The *mechanics* one — is this a
 * staged-review regeneration, which decides staging, generation source and the Cancel button
 * — stays on `isCarePlanRefresh`. This is the *enrollment* one, which decides whether the
 * approval screen collects first-time program consent or the PHA-726/897 "reviewed with
 * patient" acknowledgment, and whether approval runs completeOnboarding (the call that writes
 * `patients.consent_date`, the one-time enrollment date the billing report reads — PHA-953).
 *
 * They were the same question while regeneration was reachable only for patients with an
 * approved plan. PHA-1119 opens it to a mid-onboarding patient whose only plan is a draft:
 * their regeneration IS their onboarding. Routing them through the acknowledgment would leave
 * them with an approved plan, no consent on file and no completeOnboarding call — which is
 * precisely the hole PHA-1073 closed.
 *
 * `refreshHasApprovedPlan` is undefined outside the refresh flow, and also on the PHA-1050
 * resume path where `shouldResumeAsRefresh` has already established an approved plan exists.
 * Only an explicit `false` flips this, so anything unknown keeps the pre-PHA-1119 behaviour.
 */
export function isFirstTimeEnrollment({
  isCarePlanRefresh,
  refreshHasApprovedPlan,
}: {
  isCarePlanRefresh: boolean;
  refreshHasApprovedPlan?: boolean;
}): boolean {
  return !isCarePlanRefresh || refreshHasApprovedPlan === false;
}
